"use client";

import { useState, useEffect } from "react";
import styles from "../../Supplier Management/css/supplierManagement.module.css";
import Button from "../../../../components/Button";
import FileInput from "../../../../components/FileInput";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown";
import Modal from "../../../../components/Modal";
import Popup from "../../../../components/Popup";
import SupplierTable from "./SupplierTable";
import SupplierForm from "./SupplierForm";
import { createSupplier, fetchSuppliers, updateSupplier, uploadSupplierDoc } from "../../../../api/supplierManagement/supplierApi";

export default function SupplierFormContainer({
  showAddForm,
  setShowAddForm,
  isEditing,
  setIsEditing,
  selectedSupplier,
  setSelectedSupplier,
  suppliers,
  setSuppliers,
  activeFilter,
  setActiveFilter,
  search,
  setSearch,
  onEdit,
  onEnable,
  onDisable,
  onAddNew,
  onCancelForm
}) {
  const [popupMsg, setPopupMsg] = useState("");
  const [showEnableConfirm, setShowEnableConfirm] = useState(false);
  const [enableTarget, setEnableTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [supplierCode, setSupplierCode] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [sapcLicense, setSapcLicense] = useState("");
  const [sahpraLicense, setSahpraLicense] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [address, setAddress] = useState("");
  const [primaryContact, setPrimaryContact] = useState("");
  const [email, setEmail] = useState("");
  const [cellphone, setCellphone] = useState("");
  const [altNumber, setAltNumber] = useState("");
  const [categoriesSelected, setCategoriesSelected] = useState([]);
  const [documentName, setDocumentName] = useState("");
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (f && !documentName) setDocumentName(f.name);
  };

  // Prefill form when editing
  useEffect(() => {
    if (isEditing && selectedSupplier) {
      setSupplierCode(selectedSupplier.suppliercode || "");
      setSupplierName(selectedSupplier.supplierName || "");
      setSapcLicense(selectedSupplier.sapc || "");
      setSahpraLicense(selectedSupplier.sahpra || "");
      setVatNumber(selectedSupplier.vatnumber || "");
      setAddress(selectedSupplier.address || "");
      setPrimaryContact(selectedSupplier.contact?.name || "");
      setEmail(selectedSupplier.contact?.email || "");
      setCellphone(selectedSupplier.contact?.phone || "");
      setAltNumber(selectedSupplier.alternatenumber || "");
      setCategoriesSelected(Array.isArray(selectedSupplier.category) ? selectedSupplier.category : []);
      setFile(null);
      setDocumentName(selectedSupplier.documentname || "");
    }
  }, [isEditing, selectedSupplier]);

  // Clear form when starting a new add
  useEffect(() => {
    if (showAddForm && !isEditing) {
      setSupplierCode("");
      setSupplierName("");
      setSapcLicense("");
      setSahpraLicense("");
      setVatNumber("");
      setAddress("");
      setPrimaryContact("");
      setEmail("");
      setCellphone("");
      setAltNumber("");
      setCategoriesSelected([]);
      setFile(null);
      setDocumentName("");
    }
  }, [showAddForm, isEditing]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const mapFilterToDepartment = (f) => {
      if (f === "Dispensary") return 1;
      if (f === "Front Shop") return 2;
      if (f === "Clinic") return 3;
      return undefined;
    };
    const payload = {
      suppliercode: supplierCode,
      suppliername: supplierName,
      sapc: sapcLicense,
      sahpra: sahpraLicense,
      vatnumber: vatNumber,
      address,
      primarycontactname: primaryContact,
      email,
      cellphonenumber: cellphone,
      alternatenumber: altNumber,
      category: Array.isArray(categoriesSelected) ? categoriesSelected : [],
      departmentid: mapFilterToDepartment(activeFilter),
      status: 'Active',
    };
    (async () => {
      setSaving(true);
      let uploadedUrl = undefined;
      if (file && supplierCode) {
        try {
          uploadedUrl = await uploadSupplierDoc(supplierCode, file);
        } catch (err) {
          console.error('Supplier doc upload failed:', err);
        }
      }
      if (uploadedUrl) {
        payload.docurl = uploadedUrl;
      }
      if (documentName) {
        payload.documentname = documentName;
      }
      if (isEditing && selectedSupplier?.suppliercode) {
        await updateSupplier(selectedSupplier.suppliercode, payload);
      } else {
        await createSupplier(payload);
      }
      const data = await fetchSuppliers({ departmentid: payload.departmentid });
      const mapped = (data || []).map((s) => ({
        suppliercode: s.suppliercode,
        supplierName: s.suppliername,
        address: s.address || "",
        sapc: s.sapc || "",
        sahpra: s.sahpra || "",
        vatnumber: s.vatnumber || "",
        alternatenumber: s.alternatenumber || "",
        category: Array.isArray(s.category) ? s.category : typeof s.category === 'string' ? s.category.split(',').map(x=>x.trim()).filter(Boolean) : [],
        docurl: s.docurl || "",
        documentname: s.documentname || "",
        status: typeof s.status === 'string' ? s.status : (s.status === false ? 'Disabled' : 'Active'),
        contact: {
          name: s.primarycontactname || "",
          phone: s.cellphonenumber || "",
          email: s.email || "",
        },
        totalOrders: "N/A",
        lastSupplied: "N/A",
        disabled: typeof s.status === 'string' ? s.status.toLowerCase() !== 'active' : (s.status === false),
      }));
      setSuppliers(mapped);
      setShowAddForm(false);
      setIsEditing(false);
      setSelectedSupplier(null);
      if (onCancelForm) onCancelForm();
      setSaving(false);
    })();
  };

  const handleEdit = (row) => {
    onEdit(row);
  };

  const handleEnable = (row) => {
    onEnable(row);
  };

  const handleEnableConfirm = () => {
    onEnable(enableTarget);
    setShowEnableConfirm(false);
    setEnableTarget(null);
    setPopupMsg('✅ Supplier enabled');
  };

  if (!showAddForm) {
    return (
      <SupplierTable
        suppliers={suppliers}
        onEdit={handleEdit}
        onEnable={handleEnable}
        onAddNew={onAddNew ? onAddNew : () => setShowAddForm(true)}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        search={search}
        setSearch={setSearch}
      />
    );
  }

  return (
    <>
      <SupplierForm
        isEditing={isEditing}
        selectedSupplier={selectedSupplier}
        onSubmit={handleSubmit}
        onCancel={onCancelForm ? onCancelForm : () => { setShowAddForm(false); setIsEditing(false); setSelectedSupplier(null); }}
        onDisable={onDisable}
        onEnable={onEnable}
        saving={saving}
        supplierCode={supplierCode}
        setSupplierCode={setSupplierCode}
        supplierName={supplierName}
        setSupplierName={setSupplierName}
        sapcLicense={sapcLicense}
        setSapcLicense={setSapcLicense}
        sahpraLicense={sahpraLicense}
        setSahpraLicense={setSahpraLicense}
        vatNumber={vatNumber}
        setVatNumber={setVatNumber}
        address={address}
        setAddress={setAddress}
        primaryContact={primaryContact}
        setPrimaryContact={setPrimaryContact}
        email={email}
        setEmail={setEmail}
        cellphone={cellphone}
        setCellphone={setCellphone}
        altNumber={altNumber}
        setAltNumber={setAltNumber}
        categoriesSelected={categoriesSelected}
        setCategoriesSelected={setCategoriesSelected}
        file={file}
        setFile={setFile}
        documentName={documentName}
        setDocumentName={setDocumentName}
        handleFileChange={handleFileChange}
      />

      {showEnableConfirm && enableTarget && (
        <Modal onClose={() => { setShowEnableConfirm(false); setEnableTarget(null); }}>
          <div style={{ display: 'grid', gap: '0.5rem', minWidth: 280 }}>
            <h2 className={styles.ratingsTitle} style={{ marginBottom: '0.25rem' }}>Enable Supplier</h2>
            <p>Are you sure you want to enable <strong>{enableTarget.supplierName}</strong>?</p>
            <div className={styles.modalActionsCenter} style={{ gap: '0.5rem' }}>
              <Button
                variant="approve"
                onClick={handleEnableConfirm}
              >
                Enable
              </Button>
              <Button
                variant="secondary"
                onClick={() => { setShowEnableConfirm(false); setEnableTarget(null); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <Popup
        type="success"
        message={popupMsg}
        onClose={() => setPopupMsg("")}
      />
    </>
  );
}
